import { isPointInZone, isTableLabel, rectanglesOverlap } from "../../helpers";
import { IHasHeightAndWidth, IHasXAndY, Size } from "../../Renderer/types";
import { Zone, ZoneWithCells } from "../../types";
import { PageSaveState, PageState, State, TaxonomyMode, TaxonomyState, ZoomLevel } from "../state";
import { changeCurrentLabelOnPage } from "./changeCurrentLabel";
import { movingZones, resizingBottom, resizingLeft, resizingRight, resizingTop, updateTableCells } from "./mouseMove";

export interface KeyPressedAction {
  ctrlKey: boolean;
  shiftKey: boolean;
  key: string;
  keyCode: number;
  onSave: () => void;
  preventDefault: () => void;
  size?: Size;
  type: "KEY PRESSED";
}


const keyPressedOnPage = (state: PageState, action: KeyPressedAction, taxonomy: State["taxonomy"], cursorPosition: IHasXAndY): PageState => {
  let updatedZones: Zone[] = state.zones as Zone[];

  switch (action.key) {

    case "arrowup":
      if (action.shiftKey) {
        if (state.activeZones.length ===1) {
          updatedZones = resizingTop(
            state,
            1,
            taxonomy,
            0,
            true
          ) as Zone[];
        }
      } else if (action.ctrlKey) {
        if (state.activeZones.length ===1) {
          updatedZones = resizingBottom(
            state,
            1,
            taxonomy,
            action.size as IHasHeightAndWidth,
            0
          ) as Zone[];
        }
      }
      else {
        updatedZones = movingZones(
          state,
          0, 
          -1,
          action.size as IHasHeightAndWidth,
          taxonomy,
          0
        ) as Zone[];
      }
      return {
        ...state,
        zones: updatedZones
      }
    case "arrowright":
      if (action.shiftKey) {
        if (state.activeZones.length ===1) {
          updatedZones = resizingRight(
            state,
            -1,
            taxonomy,
            action.size as IHasHeightAndWidth,
            0,
            true
          ) as Zone[];
        }
      } else if (action.ctrlKey) {
        if (state.activeZones.length ===1) {
          updatedZones = resizingLeft(
            state,
            -1,
            taxonomy,
            0
          ) as Zone[];
        }
      } else {
        updatedZones = movingZones(
          state,
          1, 
          0,
          action.size as IHasHeightAndWidth,
          taxonomy,
          0,
        ) as Zone[];
      }
      return {
        ...state,
        zones: updatedZones
      }
    case "arrowdown":
      if (action.shiftKey) {
        if (state.activeZones.length ===1) {
          updatedZones = resizingBottom(
            state,
            -1,
            taxonomy,
            action.size  as IHasHeightAndWidth,
            0,
            true
          ) as Zone[];
        }
      } else if (action.ctrlKey) {
          if (state.activeZones.length ===1) {
          updatedZones = resizingTop(
            state,
            -1,
            taxonomy,
            0
          ) as Zone[];
        }
      } else {
        updatedZones = movingZones(
          state,
          0, 
          1,
          action.size as IHasHeightAndWidth,
          taxonomy,
          0
        ) as Zone[];
      }
      return {
        ...state,
        zones: updatedZones
      }
    case "arrowleft":
      if (action.shiftKey) {
        if (state.activeZones.length ===1) {
          updatedZones = resizingLeft(
            state,
            1,
            taxonomy,
            0,
            true
          ) as Zone[];
        }
      } else if (action.ctrlKey) {
        if (state.activeZones.length ===1) {
          updatedZones = resizingRight(
            state,
            1,
            taxonomy,
            action.size as IHasHeightAndWidth,
            0
          ) as Zone[];
        }
      } else {
        updatedZones = movingZones(
          state,
          -1, 
          0,
          action.size as IHasHeightAndWidth,
          taxonomy,
          0
        ) as Zone[];
      }
      return {
        ...state,
        zones: updatedZones
      }
    case "backspace":
    case "delete":
    case "d": {
      action.preventDefault();
      // delete the active zones
      if (state.activeZones.length < 1) {
        return state;
      }
      return {
        ...state,
        activeZones: [],
        pageSaveState: PageSaveState.Dirty,
        zones: state.zoneHistory.push(
          state.zones.filter((_, i) => state.activeZones.indexOf(i) < 0),
        ),
      };
    }
    case "y":
    case "z": {
      if (action.ctrlKey) {
        action.preventDefault();
        if ((action.key === "z" && action.shiftKey) || action.key === "y") {
          // redo action is Ctrl+Y or Shift+Ctrl+Z
          const nextZones = state.zoneHistory.redo();
          if (nextZones !== state.zones) {
            return state;
          }
          return {
            ...state,
            activeZones: [],
            pageSaveState: PageSaveState.Dirty,
            zones: nextZones,
          };
        }
        // undo action is Ctrl+Z
        const previousZones = state.zoneHistory.undo();
        if (previousZones === state.zones) {
          return state;
        }
        return {
          ...state,
          activeZones: [],
          pageSaveState: PageSaveState.Dirty,
          zones: previousZones,
        };
      }
      break;
    }
    case "a":
      if (action.ctrlKey) {
        action.preventDefault();
        const activeZones = [];
        for (let i = 0; i < state.zones.length; i++) {
          activeZones.push(i);
        }
        return {
          ...state,
          activeZones,
        };
      }
      break;
    case "s":
      if (action.ctrlKey) {
        action.preventDefault();
        if (state.pageSaveState !== PageSaveState.Dirty) {
          return state;
        }
        setTimeout(() => {
          action.onSave();
        }, 0);
      } else {
        if (state.activeZones.length > 0) {
          let anchorZone = state.zones[state.activeZones[0]];
          let minY = anchorZone.minY;
          let maxY = anchorZone.maxY;

          let newHeight = 0;
          let cursorSplit = false;

          // If user is hovering one selected zone we can split on cursor position
          const hoveredZone = state.zones.findIndex(isPointInZone(cursorPosition));
          if (state.activeZones.length === 1 && state.activeZones[0] === hoveredZone) {
            newHeight = cursorPosition.y - minY
            cursorSplit = true;
          }


          for(let zoneId of state.activeZones) {
            let zone = state.zones[zoneId];
            if (zone != anchorZone) {
              let neighborZone = state.zones.find(x => bordersShared(zone, x) >= 3)
              if (!neighborZone) {
                // disjointed zones, can't really split logically
                return state;
              }
            }

            if (zone.maxY > maxY) {
              maxY = zone.maxY;
            }
            if (zone.minY < minY) {
              minY = zone.minY;
            }          
          }

          // if we get here it's cause all the zones are stacked on top of each other.
          // so we can split n zones into n+1 zones
          if (!cursorSplit) {
            newHeight = (maxY - minY) / (state.activeZones.length + 1)
          }

          let newZones = state.zones.filter((_, i) => state.activeZones.indexOf(i) < 0);
          let newActiveZones = [];

          for (let i = 0; i < state.activeZones.length + 1; i++) {

            let subZoneMaxY = minY + ((i+1) * newHeight) - 1;
            if (cursorSplit && i === 1) {
              subZoneMaxY = maxY;
            }
            let updatedZone = {
                ...anchorZone,
                minY : minY + (i * newHeight),
                maxY : subZoneMaxY
            }

            if (isTableLabel(updatedZone.label, taxonomy) && Array.isArray(updatedZone.cells)) {
              updatedZone = {
                ...updatedZone,
                cells: updateTableCells(updatedZone.cells, updatedZone as ZoneWithCells, 2),
              };
            }

            newZones.push(updatedZone)
            newActiveZones.push(newZones.length - 1);
          }

          return {
            ...state,
            zones: state.zoneHistory.push(newZones),
            activeZones: newActiveZones
          }
        }
      }
      break;
    case "m":
      if (state.activeZones.length > 0) {
        let anchorZone = state.zones[state.activeZones[0]];
        let minY = 500000;
        let maxY = 0;
        let minX = 500000;
        let maxX = 0;

        for(let zoneId of state.activeZones) {

          let zone = state.zones[zoneId];
          // Would not know what type to select here, so for now disallow it (TODO: revisit);
          if (zone.label != anchorZone.label) {
            return state;
          }
          
          if (zone.maxY > maxY) {
            maxY = zone.maxY;
          }
          if (zone.minY < minY) {
            minY = zone.minY;
          }  
          if (zone.maxX > maxX) {
            maxX = zone.maxX;
          }
          if (zone.minX < minX) {
            minX = zone.minX;
          }          
        }
        
        let tableCells = state.zones
          .filter((_, i) => state.activeZones.indexOf(i) >= 0)
          .map(x => x.cells)
          .filter(x => x?.length > 0)
          .reduce((accumulator, value) => accumulator.concat(value), []);

        let unselectedZones = state.zones.filter((_, i) => state.activeZones.indexOf(i) < 0);
        let zoneInMergeArea = unselectedZones.find(x => {
          return rectanglesOverlap(x.minX, x.maxX, x.minY, x.maxY, minX, maxX, minY, maxY);
        })
        if (!!zoneInMergeArea) {
          return state;
        }
        let newActiveZones = [];

        unselectedZones.push({
          ...anchorZone,
          minY : minY,
          maxY : maxY,
          minX : minX,
          maxX : maxX,
          confidence: 1,
          cells: tableCells
        })
        newActiveZones.push(unselectedZones.length - 1);
        return {
          ...state,
          zones: state.zoneHistory.push(unselectedZones),
          activeZones: newActiveZones
        }
      }
      break;
  }
  return state;
};

export const bordersShared = (zone1: Zone, zone2 : Zone) => {
  let count = 0;
  
  if (Math.abs(zone1.minY - zone2.maxY) < 5) {
    count++;
  } 
  if (Math.abs(zone2.minY - zone1.maxY) < 5) {
    count++;
  }
  if (Math.abs(zone1.minX - zone2.minX) < 5) {
    count++;
  }
  if (Math.abs(zone1.maxX - zone2.maxX) < 5) {
    count++;
  }
  return count;
}

// tslint:disable-next-line: cyclomatic-complexity
export const keyPressed = (state: State, action: KeyPressedAction): State => {
  if (action.key === "l") {
    return {
      ...state,
      showZoneLabels : !state.showZoneLabels
    }
  }
  const asNumber = parseInt(action.key, 10);
  if (!isNaN(asNumber) && asNumber > 0) {
    action.preventDefault();
    const index = asNumber - 1;
    if (state.currentLabelGroup === "") {
      if (index < Object.keys(state.taxonomy.orderedLabels).length) {
        if (state.taxonomy.mode === TaxonomyMode.Complex) {
          return {
            ...state,
            currentLabelGroup: Object.keys(state.taxonomy.orderedLabels)[
              index
            ] as keyof State["taxonomy"]["orderedLabels"],
          };
        } else {
          const selectedKey = Object.keys(state.taxonomy.orderedLabels)[
            index
          ] as keyof State["taxonomy"]["orderedLabels"];
          const selectedList = state.taxonomy.orderedLabels[selectedKey];
          if (Array.isArray(selectedList) && selectedList.length > 0) {
            const updatedChangeLabelPage = changeCurrentLabelOnPage(
              state.pages[state.activePage],
              {
                label: selectedList[0],
                type: "CHANGE CURRENT LABEL",
              },
              state.taxonomy,
            );
            let changeLabelPages = state.pages;
            if (updatedChangeLabelPage !== state.pages[state.activePage]) {
              changeLabelPages = [
                ...changeLabelPages.slice(0, state.activePage),
                updatedChangeLabelPage,
                ...changeLabelPages.slice(state.activePage + 1),
              ];
            }
            return {
              ...state,
              currentLabel: selectedList[0],
              pages: changeLabelPages,
            };
          }
        }
      }
      return state;
    }
    const labelList = state.taxonomy.orderedLabels[state.currentLabelGroup];
    if (Array.isArray(labelList) && index < labelList.length) {
      const updatedChangeLabelPage = changeCurrentLabelOnPage(
        state.pages[state.activePage],
        {
          label: labelList[index],
          type: "CHANGE CURRENT LABEL",
        },
        state.taxonomy,
      );
      let changeLabelPages = state.pages;
      if (updatedChangeLabelPage !== state.pages[state.activePage]) {
        changeLabelPages = [
          ...changeLabelPages.slice(0, state.activePage),
          updatedChangeLabelPage,
          ...changeLabelPages.slice(state.activePage + 1),
        ];
      }
      return {
        ...state,
        currentLabel: labelList[index],
        currentLabelGroup: "",
        pages: changeLabelPages,
      };
    }
    return {
      ...state,
      currentLabelGroup: "",
    };
  }
  if (action.key === "+") {
    action.preventDefault();
    let newZoomLevel = state.zoom;
    switch (state.zoom) {
      case ZoomLevel["1x"]:
        newZoomLevel = ZoomLevel["1.5x"];
        break;
      case ZoomLevel["1.5x"]:
        newZoomLevel = ZoomLevel["2x"];
        break;
      case ZoomLevel["2x"]:
        newZoomLevel = ZoomLevel["2.5x"];
        break;
      case ZoomLevel["2.5x"]:
        newZoomLevel = ZoomLevel["3x"];
        break;
    }
    return {
      ...state,
      zoom: newZoomLevel,
    };
  } else if (action.key === "-") {
    action.preventDefault();
    let newZoomLevel = state.zoom;
    switch (state.zoom) {
      case ZoomLevel["1.5x"]:
        newZoomLevel = ZoomLevel["1x"];
        break;
      case ZoomLevel["2x"]:
        newZoomLevel = ZoomLevel["1.5x"];
        break;
      case ZoomLevel["2.5x"]:
        newZoomLevel = ZoomLevel["2x"];
        break;
      case ZoomLevel["3x"]:
        newZoomLevel = ZoomLevel["2.5x"];
        break;
    }
    return {
      ...state,
      zoom: newZoomLevel,
    };
  }
  const updatedPage = keyPressedOnPage(state.pages[state.activePage], action, state.taxonomy, state.pointerPosition);
  let pages = state.pages;
  if (updatedPage !== state.pages[state.activePage]) {
    pages = [
      ...state.pages.slice(0, state.activePage),
      updatedPage,
      ...state.pages.slice(state.activePage + 1),
    ];
  }
  return {
    ...state,
    currentLabelGroup: "",
    pages,
  };
};
